import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

/**
 * Blob-URLs für Videos, die gerade hochgeladen werden.
 * Zeigt sofort Video-Preview und optional Thumbnail, bis Bunny liefert.
 * Beim Entfernen werden Blob-URLs revokiert (Speicher freigeben).
 */
const PendingVideoPreviewContext = createContext<{
  getPendingPreview: (videoId: string) => string | undefined;
  getPendingThumbnail: (videoId: string) => string | undefined;
  setPendingPreview: (videoId: string, blobUrl: string) => void;
  setPendingThumbnail: (videoId: string, thumbnailBlobUrl: string) => void;
  clearPendingPreview: (videoId: string) => void;
} | undefined>(undefined);

function revokeIfNeeded(url: string, revoked: Set<string>) {
  if (!url || revoked.has(url)) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
  revoked.add(url);
}

export const PendingVideoPreviewProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const revokedRef = useRef<Set<string>>(new Set());

  const setPendingPreview = useCallback((videoId: string, blobUrl: string) => {
    setPreviews(prev => {
      const existing = prev[videoId];
      revokeIfNeeded(existing, revokedRef.current);
      return { ...prev, [videoId]: blobUrl };
    });
  }, []);

  const setPendingThumbnail = useCallback(
    (videoId: string, thumbnailBlobUrl: string) => {
      setThumbnails(prev => {
        const existing = prev[videoId];
        revokeIfNeeded(existing, revokedRef.current);
        return { ...prev, [videoId]: thumbnailBlobUrl };
      });
    },
    []
  );

  const clearPendingPreview = useCallback((videoId: string) => {
    setPreviews(prev => {
      revokeIfNeeded(prev[videoId], revokedRef.current);
      const next = { ...prev };
      delete next[videoId];
      return next;
    });
    setThumbnails(prev => {
      revokeIfNeeded(prev[videoId], revokedRef.current);
      const next = { ...prev };
      delete next[videoId];
      return next;
    });
  }, []);

  const getPendingPreview = useCallback(
    (videoId: string) => previews[videoId],
    [previews]
  );
  const getPendingThumbnail = useCallback(
    (videoId: string) => thumbnails[videoId],
    [thumbnails]
  );

  return (
    <PendingVideoPreviewContext.Provider
      value={{
        getPendingPreview,
        getPendingThumbnail,
        setPendingPreview,
        setPendingThumbnail,
        clearPendingPreview,
      }}
    >
      {children}
    </PendingVideoPreviewContext.Provider>
  );
};

export function usePendingVideoPreview() {
  const ctx = useContext(PendingVideoPreviewContext);
  if (ctx === undefined) {
    throw new Error(
      "usePendingVideoPreview must be used within PendingVideoPreviewProvider"
    );
  }
  return ctx;
}

/** Optional: gibt undefined zurück wenn Provider nicht vorhanden (z. B. in Tests). */
export function usePendingVideoPreviewOptional() {
  return useContext(PendingVideoPreviewContext);
}
